import { useState } from 'react';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new FormData();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d45ah3itkakk9n0122a0', {
        method: 'POST',
        body: new URLSearchParams({
          email: email
        }),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Newsletter Section */}
      <div className="bg-red-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Stay Updated with Electrical Tips & Offers
            </h3>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              Subscribe to our newsletter for electrical safety tips, maintenance advice, and exclusive service offers.
            </p>
            <form 
              onSubmit={handleNewsletterSubmit}
              className="max-w-md mx-auto"
              data-readdy-form
              id="newsletter"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-400 text-black px-6 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap"
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>
              {submitMessage && (
                <p className={`mt-4 text-center ${submitMessage.includes('Thank you') ? 'text-green-300' : 'text-red-300'}`}>
                  {submitMessage}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Pacifico, serif' }}>
                OhmEX Electric Inc.
              </h3>
              <p className="text-gray-300 mb-4 leading-relaxed">
                Professional electrical services in GTA. Licensed, insured, and available 24/7 for all your electrical needs.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://wa.me/16472442797"
                  className="w-10 h-10 bg-green-600 hover:bg-green-700 rounded-full flex items-center justify-center transition-colors duration-300"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                </a>
                <a
                  href="tel:+16472442797"
                  className="w-10 h-10 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center transition-colors duration-300"
                >
                  <i className="ri-phone-line text-lg"></i>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <a href="/" className="text-gray-300 hover:text-yellow-400 transition-colors duration-300">
                    Home
                  </a>
                </li>
                <li>
                  <a href="/about" className="text-gray-300 hover:text-yellow-400 transition-colors duration-300">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="/services" className="text-gray-300 hover:text-yellow-400 transition-colors duration-300">
                    Services
                  </a>
                </li>
                <li>
                  <a href="/gallery" className="text-gray-300 hover:text-yellow-400 transition-colors duration-300">
                    Gallery
                  </a>
                </li>
                <li>
                  <a href="/contact" className="text-gray-300 hover:text-yellow-400 transition-colors duration-300">
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Our Services</h4>
              <ul className="space-y-2">
                <li>
                  <a href="/services" className="text-gray-300 hover:text-yellow-400 transition-colors duration-300">
                    Panel Upgrades
                  </a>
                </li>
                <li>
                  <a href="/services" className="text-gray-300 hover:text-yellow-400 transition-colors duration-300">
                    EV Charger Installation
                  </a>
                </li>
                <li>
                  <a href="/services" className="text-gray-300 hover:text-yellow-400 transition-colors duration-300">
                    Potlight Installation
                  </a>
                </li>
                <li>
                  <a href="/services" className="text-gray-300 hover:text-yellow-400 transition-colors duration-300">
                    Emergency Services
                  </a>
                </li>
                <li>
                  <a href="/services" className="text-gray-300 hover:text-yellow-400 transition-colors duration-300">
                    Commercial Electrical
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact & Hours */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact & Hours</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <i className="ri-map-pin-line text-yellow-400 mt-1"></i>
                  <span className="text-gray-300">32 Poplar Plains Road, Brampton, Ontario L7A 1Z5, Canada</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-phone-line text-yellow-400"></i>
                  <a href="tel:+16472442797" className="text-gray-300 hover:text-yellow-400 transition-colors duration-300">
                    (647) 244-2797
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-whatsapp-line text-yellow-400"></i>
                  <a href="https://wa.me/16472442797" className="text-gray-300 hover:text-yellow-400 transition-colors duration-300">
                    WhatsApp
                  </a>
                </div>
              </div>
              
              <div className="mt-6">
                <h5 className="font-semibold mb-2 text-yellow-400">Business Hours</h5>
                <div className="text-sm text-gray-300 space-y-1">
                  <div className="flex justify-between">
                    <span>Monday - Friday:</span>
                    <span>7:00 AM - 8:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday:</span>
                    <span>8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday:</span>
                    <span>Emergency Only</span>
                  </div>
                  <div className="text-yellow-400 font-semibold mt-2">
                    24/7 Emergency Service Available
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2024 OhmEX Electric Inc. All rights reserved. Licensed Electrical Contractor in Ontario.
            </p>
            <a 
              href="https://readdy.ai/?origin=logo" 
              className="text-gray-400 hover:text-yellow-400 text-sm transition-colors duration-300 mt-2 md:mt-0"
            >
              Website Builder
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
